import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  checkStaffId,
  checkStaffMobilePhone,
  addCampusToStaff,
  getStaff,
  getStaffs,
  createStaff,
  updateStaff,
  updatePassword,
} from '../api/employees.api';
import {
  IStaff,
  IGetStaff,
  IStaffList,
  IGetStaffs,
  IAddCampusReq,
  IStaffParams,
} from '../models/employees.model';

interface IStaffState {
  staff?: IStaff|undefined;
  staffs?: IStaffList[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: IStaffState = {
  staff: undefined,
  staffs: [],
  isLoading: false,
};

const staffSlicer = createSlice({
  name: 'staff',
  initialState,
  reducers: {
    getStaffStart: (state: IStaffState) => {
      state.staff = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getStaffSuccess: (state: IStaffState, action: PayloadAction<IGetStaff>) => {
      state.staff = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getStaffFailure: (
      state: IStaffState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.staff = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    setStaffNone: (state: IStaffState) => {
      state.staff = undefined;
    },
    getStaffsStart: (state: IStaffState) => {
      state.staffs = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getStaffsSuccess: (
      state: IStaffState,
      action: PayloadAction<IGetStaffs>,
    ) => {
      state.staffs = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getStaffsFailure: (
      state: IStaffState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.staffs = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: IStaffState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: IStaffState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    requestSuccess: (state: IStaffState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    createSuccess: (state: IStaffState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: IStaffState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getStaffStart,
  getStaffSuccess,
  getStaffFailure,
  getStaffsStart,
  getStaffsSuccess,
  getStaffsFailure,
  setStaffNone,
  requestStart,
  requestFailure,
  requestSuccess,
  createSuccess,
  updateSuccess,
} = staffSlicer.actions;

export default staffSlicer.reducer;

export const doGetStaff =
  (memberCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getStaffStart());
      const response = await getStaff(memberCode);
      dispatch(getStaffSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getStaffFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetStaffs =
  (params?: IStaffParams): any =>
  async (dispatch: any) => {
    try {
      dispatch(getStaffsStart());
      const response = await getStaffs(params);
      dispatch(getStaffsSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getStaffsFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCheckStaffId =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      const response = await checkStaffId(id);
      dispatch(requestSuccess());
      return response.data.data.dupYn === 'Y'
        ? { status: 400 }
        : { status: 200 };
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCheckStaffMobilePhone =
  (phone: string): any =>
  async (dispatch: any) => {
    try {
      const response = await checkStaffMobilePhone(phone);
      dispatch(requestSuccess());
      return response.data.data.dupYn === 'Y'
        ? { status: 400 }
        : { status: 200 };
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doAddCampusToStaff =
  (id: string, data: IAddCampusReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await addCampusToStaff(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateStaff =
  (data: any): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createStaff(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateStaff =
  (id: string, data: any): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateStaff(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdatePassword =
  (id: string, data: any): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updatePassword(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
